export const PROGRAMME_JOURNEYS = Object.freeze({
  zone: {
    key: 'zone',
    slug: 'experimentation-zone',
    title: 'Experimentation Zone',
    shortName: 'the Zone',
    audienceName: 'Applicant',
    portalName: 'Zone portal',
    tagline: 'Where the rules are tested',
    description:
      'The LabZone is Barbados’ proving ground—a place to run real-world experiments where regulation is rigid, undefined or still emerging.',
    descriptionSecondary:
      'Here, mission-driven solutions are tested within national systems, under public oversight, and developed into evidence government can use.',
    seoDescription:
      'Apply to the Supernova Lab Experimentation Zone, track your status and access your research credential and programme updates.',
    accent: 'teal',
    applyLabel: 'Apply to the Zone',
    loginLabel: 'Applicant login',
    publicSectionTitle: 'Built for applied experimentation',
    publicSectionCopy:
      'A clear path for teams moving from a promising idea to a responsibly designed real-world experiment.',
    publicPoints: [
      'A structured review and scoping process',
      'Clear research guardrails and responsibilities',
      'A secure portal to track the application and receive updates',
    ],
    publicSteps: [
      {
        title: 'Apply',
        description: 'Tell us about your team, the idea and what you need to learn.',
        icon: 'document',
      },
      {
        title: 'Review',
        description: 'The programme team reviews fit, readiness and safeguards.',
        icon: 'users',
      },
      {
        title: 'Enter the Zone',
        description:
          'Approved teams receive onboarding, participant resources and access to their research credential.',
        icon: 'rocket',
      },
    ],
    secondaryTitle: 'Experimentation themes',
    secondaryCopy:
      'Explore the areas where teams can test ideas, build evidence and learn in real settings.',
    themes: [
      { title: 'Trust and safety', icon: 'shield' },
      { title: 'Knowledge systems', icon: 'signal' },
      { title: 'Human–AI collaboration', icon: 'users' },
      { title: 'Sustainable futures', icon: 'leaf' },
      { title: 'Emerging technology', icon: 'blocks' },
    ],
    applicationIntro:
      'Share enough detail for the programme team to understand the experiment, its intended users and the safeguards it may require.',
    applicationPreparation: {
      summary:
        'The application has three sections and a final review. You can return to earlier sections before submitting.',
      items: [
        'Team contact and organisation details',
        'The experiment question, stage and intended participants',
        'Expected timing, safeguards and known approvals',
      ],
    },
    applicationSteps: [
      {
        title: 'Your team',
        fields: [
          {
            name: 'fullName',
            label: 'Full name',
            type: 'text',
            autoComplete: 'name',
            placeholder: 'Your full name',
            required: true,
            validationMessage: 'Enter your full name.',
          },
          {
            name: 'email',
            label: 'Work email',
            type: 'email',
            autoComplete: 'email',
            placeholder: 'name@organisation.com',
            required: true,
            validationMessage: 'Enter your work email.',
          },
          {
            name: 'organisation',
            label: 'Organisation',
            type: 'text',
            autoComplete: 'organization',
            placeholder: 'Organisation or team name',
            required: true,
            validationMessage: 'Enter your organisation or team name.',
          },
          {
            name: 'role',
            label: 'Your role',
            type: 'text',
            autoComplete: 'organization-title',
            placeholder: 'Role in the proposed experiment',
            required: true,
            validationMessage:
              'Describe your role in the proposed experiment.',
          },
        ],
      },
      {
        title: 'The experiment',
        fields: [
          {
            name: 'challenge',
            label: 'What are you trying to test?',
            type: 'textarea',
            placeholder: 'Describe the challenge, proposed intervention and what you need to learn.',
            required: true,
            validationMessage: 'Describe what you want to test.',
            wide: true,
          },
          {
            name: 'stage',
            label: 'Current stage',
            type: 'select',
            options: ['Early concept', 'Prototype', 'Pilot-ready', 'Already testing'],
            required: true,
            validationMessage: 'Select the experiment’s current stage.',
          },
          {
            name: 'participants',
            label: 'Who may be involved?',
            type: 'text',
            placeholder: 'Users, communities or organisations',
            required: true,
            validationMessage: 'Tell us who may participate.',
          },
        ],
      },
      {
        title: 'Readiness',
        fields: [
          {
            name: 'timeline',
            label: 'Preferred timeline',
            type: 'select',
            options: ['Within 3 months', '3–6 months', '6–12 months', 'Still exploring'],
            required: true,
            validationMessage: 'Select your preferred timeline.',
          },
          {
            name: 'safeguards',
            label: 'What safeguards or approvals may be needed?',
            type: 'textarea',
            placeholder: 'Share known legal, ethical, data, safety or participation considerations.',
            required: true,
            validationMessage:
              'Describe the safeguards or approvals you have identified.',
            wide: true,
          },
        ],
      },
    ],
  },
  fellowship: {
    key: 'fellowship',
    slug: 'fellowships',
    title: 'Fellowships',
    shortName: 'the fellowship',
    audienceName: 'Fellow',
    portalName: 'Fellowship portal',
    tagline: 'Build what comes next.',
    description:
      'A programme and cohort space for mission-driven practitioners to learn through doing, develop their work and share what they discover.',
    seoDescription:
      'Apply for a Supernova Lab Fellowship and access a dedicated cohort space for programme updates, onboarding and resources.',
    accent: 'purple',
    applyLabel: 'Apply for a fellowship',
    loginLabel: 'Fellow login',
    publicSectionTitle: 'A cohort for applied learning',
    publicSectionCopy:
      'A supported programme journey that combines independent practice, shared learning and a community of peers.',
    publicPoints: [
      'A purposeful cohort experience',
      'A secure portal to track the application and receive updates',
      'Onboarding, cohort connections and resources after selection',
    ],
    publicSteps: [
      {
        title: 'Apply',
        description: 'Share your focus, experience and what you want to build.',
        icon: 'edit',
      },
      {
        title: 'Join the cohort',
        description: 'Selected fellows complete onboarding and meet their peers.',
        icon: 'users',
      },
      {
        title: 'Build and share',
        description: 'Learn through practice and share useful work with the community.',
        icon: 'spark',
      },
    ],
    secondaryTitle: 'Programme journey',
    secondaryCopy:
      'Selected fellows receive cohort updates, onboarding materials and programme resources in one place.',
    themes: [
      { title: 'Applications', icon: 'document' },
      { title: 'Selection', icon: 'users' },
      { title: 'Onboarding', icon: 'compass' },
      { title: 'Fellowship', icon: 'spark' },
    ],
    applicationIntro:
      'Tell us about your focus, the perspective you bring and how a fellowship could support the work you want to do.',
    applicationPreparation: {
      summary:
        'The application has three sections and a final review. You can return to earlier sections before submitting.',
      items: [
        'Your contact, location and current practice',
        'The work you want to explore and why it matters now',
        'Your general availability and optional participation needs',
      ],
    },
    applicationSteps: [
      {
        title: 'About you',
        fields: [
          {
            name: 'fullName',
            label: 'Full name',
            type: 'text',
            autoComplete: 'name',
            placeholder: 'Your full name',
            required: true,
            validationMessage: 'Enter your full name.',
          },
          {
            name: 'email',
            label: 'Email address',
            type: 'email',
            autoComplete: 'email',
            placeholder: 'name@example.com',
            required: true,
            validationMessage: 'Enter your email address.',
          },
          {
            name: 'location',
            label: 'Location',
            type: 'text',
            autoComplete: 'country-name',
            placeholder: 'City and country',
            required: true,
            validationMessage: 'Enter your city and country.',
          },
          {
            name: 'role',
            label: 'Current role or practice',
            type: 'text',
            placeholder: 'How you describe your work',
            required: true,
            validationMessage:
              'Tell us how you describe your current role or practice.',
          },
        ],
      },
      {
        title: 'Your focus',
        fields: [
          {
            name: 'focus',
            label: 'What do you want to explore or build?',
            type: 'textarea',
            placeholder: 'Describe the area of work you want to develop through the fellowship.',
            required: true,
            validationMessage:
              'Describe what you want to explore or build.',
            wide: true,
          },
          {
            name: 'whyNow',
            label: 'Why is this the right time?',
            type: 'textarea',
            placeholder: 'Tell us why this work matters now.',
            required: true,
            validationMessage: 'Explain why this is the right time.',
            wide: true,
          },
        ],
      },
      {
        title: 'Participation',
        fields: [
          {
            name: 'availability',
            label: 'General availability',
            type: 'select',
            options: ['Weekday daytime', 'Weekday evenings', 'Weekends', 'Flexible'],
            required: true,
            validationMessage: 'Select your general availability.',
          },
          {
            name: 'accessNeeds',
            label: 'Access or participation needs',
            type: 'textarea',
            placeholder: 'Optional information that would help us support your participation.',
            required: false,
            wide: true,
          },
        ],
      },
    ],
  },
});

export const getProgramme = (kind) =>
  PROGRAMME_JOURNEYS[kind] || PROGRAMME_JOURNEYS.zone;
